import { defineStore } from 'pinia'
import { useChatStore, type ChatThread } from '@/stores/chat'

type ProjectId = number | string
type LngLat = [number, number]

// Project discussion (Chat tab) UI state. All conversation/message data lives
// in the `chat` store — this store keeps only the map-interaction state
// (pin-drop mode, drafts, the active on-map thread) and delegates the rest,
// preserving the getter/action surface the components already use.
export const useDiscussionStore = defineStore('discussion', {
  state: () => ({
    enabledByProject: {} as Record<string, boolean>, // String(projectId) -> boolean; absent = enabled
    // "click the map to place" mode: null | 'composer' (location goes to the
    // chat composer) | 'map' (opens the on-map compose bubble)
    pinDrop: null as 'composer' | 'map' | null,
    draftLocation: null as LngLat | null, // [lng, lat] captured for the chat composer
    mapDraft: null as LngLat | null,      // [lng, lat] where the on-map compose bubble is open
    mapDraftGeometry: null as any, // GeoJSON drawn for the pending on-map message
    activeThreadId: null as number | null, // message currently open in the map popup
  }),

  getters: {
    messages: () => (projectId: ProjectId): ChatThread[] => {
      const chat = useChatStore()
      const uuid = chat.projectUuid(projectId)
      return uuid ? chat.threads(uuid) : []
    },
    pendingAgent: () => (projectId: ProjectId) => {
      const chat = useChatStore()
      const uuid = chat.projectUuid(projectId)
      return uuid ? chat.pendingAgent(uuid) : null
    },
    isEnabled: (state) => (projectId: ProjectId) => state.enabledByProject[String(projectId)] !== false,
    activeThread() {
      return (projectId: ProjectId): ChatThread | null =>
        this.messages(projectId).find((m) => m.id === this.activeThreadId) || null
    },
    threadForFeature() {
      return (projectId: ProjectId, featureCode: string): ChatThread | null =>
        this.messages(projectId).find((m) => m.featureCode === featureCode) || null
    },
  },

  actions: {
    setEnabled({ projectId, enabled }: { projectId: ProjectId, enabled: boolean }) {
      this.enabledByProject[String(projectId)] = enabled
    },
    setPinDrop(value: 'composer' | 'map' | null) {
      this.pinDrop = value || null
    },
    setDraftLocation(location: LngLat | null) {
      this.draftLocation = location
    },
    // Accepts [lng, lat] or { location, geometry }; null clears both.
    setMapDraft(payload: LngLat | { location?: LngLat | null, geometry?: any } | null) {
      this.mapDraft = Array.isArray(payload) ? payload : payload?.location || null
      this.mapDraftGeometry = (payload && !Array.isArray(payload) && payload.geometry) || null
    },
    setActiveThread(messageId: number | null) {
      this.activeThreadId = messageId
    },

    // Resolve the project's conversation and load its messages.
    async ensureLoaded(projectId: ProjectId | null | undefined) {
      if (!projectId) return
      try {
        await useChatStore().ensureProjectConversation(projectId)
      } catch (e) {
        // Not a member / project gone — the feed just stays empty.
      }
    },
    async sendMessage(
      { projectId, text, mentions, attachments, location, geometry, featureCode }:
      { projectId: ProjectId, text?: string, mentions?: any[], attachments?: any[], location?: any, geometry?: any, featureCode?: string | null }
    ) {
      const chat = useChatStore()
      const uuid = await chat.ensureProjectConversation(projectId)
      const message = await chat.sendMessage({
        uuid,
        text,
        mentions,
        attachments,
        location: location ?? this.draftLocation,
        geometry: geometry ?? (featureCode ? null : this.mapDraftGeometry),
        featureCode,
      })
      this.draftLocation = null
      return message
    },
    async sendReply({ projectId, messageId, text, mentions, attachments }: { projectId: ProjectId, messageId: number, text?: string, mentions?: any[], attachments?: any[] }) {
      const chat = useChatStore()
      const uuid = chat.projectUuid(projectId)
      if (!uuid) return null
      return chat.sendMessage({ uuid, text, mentions, attachments, parentId: messageId })
    },
    // Agent replies are triggered server-side; this only shows the typing
    // indicator until the reply lands over WS.
    notePendingAgent({ projectId, name }: { projectId: ProjectId, name?: string | null }) {
      const chat = useChatStore()
      const uuid = chat.projectUuid(projectId)
      if (!uuid) return
      chat.notePendingAgent({ uuid, name })
    },
  },
})
